import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useApplyPreviewMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useApplyPreviewMutation";
import useCreateEmailThreadQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useCreateEmailThreadQuery";
import ChartStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/chart/chartService";
import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import { addVersionMessage, setMessages } from "app/pages/sales-intelligence/modules/ai-agent/store";
import type {
    AiAgentEmailPayload,
    AiAgentEmailAutocompleteCompany,
} from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import type { VersionThreadMessage } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";

type UseEmailComposerReturn = {
    recipient: Contact | null;
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    activeCompany: AiAgentEmailAutocompleteCompany | null;
    subject: string;
    body: string;
    chart: Record<string, unknown> | null;
    chartAnchorIndex: number;
    setChartAnchorIndex: Dispatch<SetStateAction<number>>;
    emailPreviews: ParsedAIEmailPreview[];
    isLoading: boolean;
    hasPayload: boolean;
    setRecipient: Dispatch<SetStateAction<Contact | null>>;
    onCompanySelect: (company: AiAgentEmailAutocompleteCompany) => void;
    setSubject: Dispatch<SetStateAction<string>>;
    setBody: Dispatch<SetStateAction<string>>;
    onGenerate: () => void;
    isGenerateAvailable: boolean;
    threadId: string;
    messageId: string;
    onAddVersion: (version: ParsedAIEmailPreview) => void;
    graphImg: string;
    setGraphImg: Dispatch<SetStateAction<string>>;
    versions: ParsedAIEmailPreview[];
    isUpdatingVersion: boolean;
    setIsUpdatingVersion: Dispatch<SetStateAction<boolean>>;
    onStop: () => void;
    onSetEmailPreviews: (previews: ParsedAIEmailPreview[]) => void;
};

export const useEmailComposer = (): UseEmailComposerReturn => {
    const [recipient, setRecipient] = useState<Contact | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<AiAgentEmailAutocompleteCompany | null>(null);
    const [activeCompany, setActiveCompany] = useState<AiAgentEmailAutocompleteCompany | null>(null);
    const [payload, setPayload] = useState<AiAgentEmailPayload | null>(null);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [lastRequestKey, setLastRequestKey] = useState<string | null>(null);
    const [chart, setChart] = useState<Record<string, unknown> | null>(null);
    const [chartAnchorIndex, setChartAnchorIndex] = useState(-1);
    const [graphImg, setGraphImg] = useState<string>("");
    const [versions, setVersions] = useState<ParsedAIEmailPreview[]>([]);
    const [isUpdatingVersion, setIsUpdatingVersion] = useState(false);

    const { mutate } = useApplyPreviewMutation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { isContactsVisible, isContactsStatusFetched } = useIsContactsAvailable();
    const shouldClearRecipientOnCompanyChange = isContactsStatusFetched && isContactsVisible;

    const {
        isLoading,
        email: generatedEmail,
        emailPreviews,
        reset,
        refetch,
        threadId,
        messageId,
        stop,
        onSetEmailPreviews,
    } = useCreateEmailThreadQuery(payload);

    const currentKey = useMemo(() => {
        const domain = selectedCompany?.domain ?? "";
        return `${domain}|${recipient?.name.trim()}|${selectedCompany?.isCompanyLevel}`;
    }, [selectedCompany, recipient]);

    const hasContent = Boolean(generatedEmail?.subject && generatedEmail?.body);
    const isDirty = useMemo(() => lastRequestKey !== currentKey, [lastRequestKey, currentKey]);

    const isGenerateAvailable = useMemo(
        () => Boolean(selectedCompany) && (!isLoading || hasContent || isDirty),
        [selectedCompany, hasContent, isDirty, isLoading],
    );

    const createVersionMessage = useCallback(
        (version: ParsedAIEmailPreview): VersionThreadMessage => {
            let chartTitle = "";
            if (ChartStreamService.isChartObject(version.chartJson)) {
                chartTitle = version.chartJson.title?.text;
            }
            const content = chartTitle || subject || "";
            const versionMessage: VersionThreadMessage = {
                type: "version",
                content,
                id: versions.length?.toString(),
                isDisabled: false,
            };
            return versionMessage;
        },
        [subject, versions.length],
    );

    useEffect(() => {
        if (generatedEmail) {
            setSubject(generatedEmail.subject);
            setBody(generatedEmail.body);
            setChart(generatedEmail.chartJson);
            setChartAnchorIndex(generatedEmail.chartAnchorIndex ?? -1);
            setVersions([generatedEmail]);
            const message = createVersionMessage(generatedEmail);
            dispatch(addVersionMessage({ message, disabledVersions: [] }));
        }
    }, [generatedEmail]);

    const resetState = () => {
        setSubject("");
        setBody("");
        setChart(null);
        setChartAnchorIndex(-1);
        setGraphImg("");
        setVersions([]);
        dispatch(setMessages([]));
    };

    const onGenerate = useCallback(() => {
        if (!selectedCompany) {
            return;
        }
        setActiveCompany(selectedCompany);
        resetState();
        void queryClient.cancelQueries({ queryKey: aiAgentQueryKeys.contactsAll() });
        const emailContext = {
            attendees: `${recipient?.name || ""}${recipient?.title ? `, ${recipient?.title}` : ""}`,
            isCompanyLevel: Boolean(selectedCompany.isCompanyLevel),
        };
        setPayload({
            domain: selectedCompany.domain,
            companyName: selectedCompany?.companyName,
            emailContext,
        });
        setLastRequestKey(currentKey);
        reset();
        if (!isDirty) {
            void refetch();
        }
    }, [selectedCompany, recipient, currentKey, isDirty, refetch, reset]);

    const onCompanySelect = (company: AiAgentEmailAutocompleteCompany) => {
        setSelectedCompany(company);
        if (shouldClearRecipientOnCompanyChange) {
            setRecipient(null);
        }
    };

    const applyPreviewMutation = (emailPreview: ParsedAIEmailPreview) => {
        const text = emailPreview.body || "";
        const textToInsert = JSON.stringify(emailPreview.chartJson) || "";
        const bodyText =
            text.slice(0, emailPreview.chartAnchorIndex) + textToInsert + text.slice(emailPreview.chartAnchorIndex);
        mutate({ threadId, content: JSON.stringify({ subject: emailPreview.subject, body: bodyText }) });
    };

    const updatePreview = (emailPreview: ParsedAIEmailPreview, withApplyMutation = false) => {
        setIsUpdatingVersion(true);
        setSubject(emailPreview.subject);
        setBody(emailPreview.body);

        const isSameChart =
            ParseJSONService.areSameJSON(emailPreview.chartJson, chart) &&
            emailPreview.chartAnchorIndex === chartAnchorIndex;
        if (emailPreview.chartJson && typeof emailPreview.chartJson === "object") {
            setChart(emailPreview.chartJson);
            setChartAnchorIndex(emailPreview.chartAnchorIndex ?? -1);
        } else {
            setChart(null);
            setChartAnchorIndex(-1);
        }
        if (!isSameChart) {
            setGraphImg("");
        }
        if (!emailPreview.chartJson || isSameChart) {
            setTimeout(() => setIsUpdatingVersion(false), 300);
        }

        if (withApplyMutation) {
            applyPreviewMutation(emailPreview);
        }
    };

    const onAddVersion = useCallback(
        (version: ParsedAIEmailPreview, withApplyMutation = false) => {
            if (!version) {
                return;
            }

            setVersions((prev) => {
                const newVersionMessage = createVersionMessage(version);
                const disabledVersions = prev
                    .map((v, i) => (ParseJSONService.areSamePreview(v, version) ? i.toString() : null))
                    .filter((id): id is string => id !== null);
                dispatch(addVersionMessage({ message: newVersionMessage, disabledVersions }));
                updatePreview(version, withApplyMutation);

                return [...prev, version];
            });
        },
        [createVersionMessage, updatePreview],
    );

    const onStop = () => {
        setIsUpdatingVersion(false);
        void stop();
    };

    return {
        recipient,
        selectedCompany,
        activeCompany,
        subject,
        body,
        chart,
        chartAnchorIndex,
        setChartAnchorIndex,
        emailPreviews,
        onSetEmailPreviews,
        isLoading,
        hasPayload: Boolean(payload),
        setRecipient,
        onCompanySelect,
        setSubject,
        setBody,
        onGenerate,
        isGenerateAvailable,
        threadId,
        messageId,
        onAddVersion,
        graphImg,
        setGraphImg,
        versions,
        isUpdatingVersion,
        setIsUpdatingVersion,
        onStop,
    };
};
